import {ValidatedCheckboxField} from 'common';
import {Checkbox, Pane, Text} from 'evergreen-ui';
import {FeatureFlaggable, ProviderPortalFeatureFlag, useFeatureFlagFilter} from 'featureflag';
import React, {memo} from 'react';
import {Controller, useFormContext, useWatch} from 'react-hook-form';


type Prop = {
    canSetUserAdmin?: boolean;
    canSetClientReport?: boolean;
}

/**
 * One box in an access group. Two properties hide a box, each for its own reason.
 * `gated` hides it unless the granter may set Client Care. That is about who is granting.
 * `featureFlag` hides it wherever the environment has that feature switched off.
 * `children` render indented under the box and show or hide with it.
 * A child needs its parent: ticking a child ticks the parent, and unticking the parent clears the children.
 */
type AccessChild = FeatureFlaggable<{
    name: string;
    label: string;
    testId: string;
    gated?: boolean;
    children?: AccessChild[];
}>;

const CLIENT_MANAGEMENT: AccessChild[] = [
    {name: 'clientPlacementAccess', label: 'Client Placement', testId: 'um-referral-management-input'},
    {name: 'clientReportAccess', label: 'Client Care', testId: 'um-client-report-input', gated: true},
];

// Both flag names read backwards, so featureFlagNegate keeps the box while the feature is on.
const BUSINESS_MANAGEMENT: AccessChild[] = [
    {name: 'providerMetricsAccess', label: 'Provider Metrics', testId: 'um-provider-metrics-input', gated: true},
    {name: 'rosterAccess', label: 'Roster Management', testId: 'um-roster-input', gated: true},
    {name: 'auditLogAccess', label: 'Audit Log', testId: 'um-audit-log-input',
        featureFlag: ProviderPortalFeatureFlag.TDTL_9900_IS_EXPORT_DISABLED, featureFlagNegate: true},
    {name: 'contactReadAccess', label: 'Contact Management', testId: 'um-contact-read-input',
        featureFlag: ProviderPortalFeatureFlag.BHPNPP_3240_IS_BIZ_CONTACT_DISABLED, featureFlagNegate: true,
        children: [{name: 'contactEditAccess', label: 'Edit Permission', testId: 'um-contact-edit-input'}]},
];

type AccessGroupProp = {
    label: string;
    testId: string;
    items: AccessChild[];
};

/**
 * A labelled section whose header is an FE-only "select all" shortcut. It persists nothing.
 * It shows ticked while any visible box under it is ticked, nested boxes included.
 * Clicking it ticks every box when none is ticked, and clears every box otherwise.
 */
const AccessGroup = ({label, testId, items}: AccessGroupProp) => {
    const {control, setValue} = useFormContext();
    const boxes = items.flatMap((item) => [item, ...(item.children ?? [])]);
    const names = boxes.map((box) => box.name);
    const values = useWatch({control, name: names}) as Array<boolean | undefined>;
    const checkedByName = new Map(names.map((name, index) => [name, !!values[index]]));
    const anyChecked = values.some(Boolean);
    const set = (name: string, checked: boolean) => setValue(name, checked, {shouldDirty: true});
    // ValidatedCheckboxField lets a passed onChange replace its form binding, so these boxes write the
    // form through setValue.
    const renderBox = (box: AccessChild, onChange: (checked: boolean) => void) => (
        <Checkbox
            key={box.name}
            size={400}
            marginY={12}
            checked={checkedByName.get(box.name) ?? false}
            data-testid={box.testId}
            onChange={(e) => onChange(e.target.checked)}
            label={<span className='text-sm font-normal'>{box.label}</span>}/>
    );
    return (
        <>
            <Checkbox
                size={400}
                marginY={12}
                checked={anyChecked}
                data-testid={testId}
                onChange={() => names.forEach((name) => set(name, !anyChecked))}
                label={<span className='text-sm font-semibold'>{label}</span>}/>
            <Pane paddingLeft={24}>
                {items.map((item) => (
                    <React.Fragment key={item.name}>
                        {renderBox(item, (checked) => {
                            set(item.name, checked);
                            if (!checked) {
                                item.children?.forEach((child) => set(child.name, false));
                            }
                        })}
                        {item.children && (
                            <Pane paddingLeft={24}>
                                {item.children.map((child) => renderBox(child, (checked) => {
                                    set(child.name, checked);
                                    if (checked) {
                                        set(item.name, true);
                                    }
                                }))}
                            </Pane>
                        )}
                    </React.Fragment>
                ))}
            </Pane>
        </>
    );
};

export const AppAccessInput = memo(function AppAccessInput({canSetUserAdmin = false, canSetClientReport = false}: Prop) {
    const {control} = useFormContext();
    // The flag filter is a hook, so each list gets its own top-level call rather than a shared helper.
    const clientFlagged = useFeatureFlagFilter(CLIENT_MANAGEMENT);
    const businessFlagged = useFeatureFlagFilter(BUSINESS_MANAGEMENT);
    const clientItems = clientFlagged.filter((item) => !item.gated || canSetClientReport);
    const businessItems = businessFlagged.filter((item) => !item.gated || canSetClientReport);

    // BHPNPP-3110
    // Read-only mirror: external admins can see but not change User Administration
    // (Catalight-managed). Stays registered in the form so the payload echoes the
    // current value back — the BE validates it as a no-change assertion.
    // This avoids future UI / UX confusion
    return (
        <Pane paddingLeft={24} width='100%'>
            <Text size={500}>Access</Text>
            <Checkbox
                size={400}
                marginY={12}
                disabled
                checked
                data-testid="um-portal-access-input"
                label={<span className="text-sm font-normal">Portal Access</span>}/>
            <AccessGroup label='Client Management' testId='um-client-management-input' items={clientItems}/>
            {businessItems.length > 0 &&
                <AccessGroup label='Business Management' testId='um-business-management-input' items={businessItems}/>}
            {canSetUserAdmin ? (
                <ValidatedCheckboxField
                    size={400}
                    marginY={12}
                    name='userManagementAdminAccess'
                    data-testid="um-user-administration-input"
                    label={<span className='text-sm font-normal'>User Administration</span>}/>
            ) : (
                <Controller
                    name="userManagementAdminAccess"
                    control={control}
                    render={({field: {value}}) => {
                        return (
                            <Checkbox
                                size={400}
                                marginY={12}
                                disabled
                                checked={value}
                                data-testid="um-user-administration-input"
                                label={<span className='text-sm font-normal'>User Administration</span>}/>
                        );
                    }}
                />
            )}
        </Pane>
    );
});
